import React, { useState, useEffect, useRef } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  Chip, CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { FS_MICRO, ICON_LG } from '../../common/theme'
import { FancyButton } from '../../comps/FancyButtons'
import {
  type KbDocPage, type KbDocSource,
  fileExt, fetchDocSections, saveDocPage,
} from '../../services/KnowledgeBaseService'

// ─── Editable doc page pane ───────────────────────────────────────────────────

function DocPageEditor({ page, onSave }: { page: KbDocPage; onSave: (page: KbDocPage, html: string) => Promise<void> }) {
  const [savedHtml, setSavedHtml] = useState('')
  const [html, setHtml] = useState('')
  const [saving, setSaving] = useState(false)
  const divRef = useRef<HTMLDivElement>(null)
  const isDirty = html !== savedHtml

  useEffect(() => {
    if (divRef.current) {
      divRef.current.innerHTML = page.htmlcontent
      const normalized = divRef.current.innerHTML
      setSavedHtml(normalized)
      setHtml(normalized)
    }
  }, [page.pagesha])

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(page, html)
      setSavedHtml(html)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box data-testid="dp_page_editor" data-page-sha={page.pagesha} data-page-num={page.page}
      sx={{ flex: 1, p: 1.5, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: FS_MICRO }}>
        Extracted Text
      </Typography>
      <Box
        ref={divRef}
        component="div"
        contentEditable
        suppressContentEditableWarning
        data-testid="dp_page_editor_input"
        onInput={(e: React.FormEvent<HTMLDivElement>) => setHtml(e.currentTarget.innerHTML)}
        sx={{
          fontSize: '0.8rem', lineHeight: 1.6, color: 'text.primary',
          border: '1px solid transparent', borderRadius: 0.5, px: 0.5,
          minHeight: 80,
          '& p': { m: 0, mb: 0.5 },
          '&:focus': { outline: 'none', borderColor: 'primary.main' },
        }}
      />
      {isDirty && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FancyButton solid testId="dp_page_save_btn"
            disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save'}
          </FancyButton>
        </Box>
      )}
    </Box>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface DocPreviewModalProps {
  open: boolean
  docsha: string | null
  onClose: () => void
}

export default function DocPreviewModal({ open, docsha, onClose }: DocPreviewModalProps) {
  const [loading, setLoading] = useState(false)
  const [docPreview, setDocPreview] = useState<KbDocSource | null>(null)
  const [thumbWidth, setThumbWidth] = useState(200)

  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(200)

  // Fetch the doc (or a specific page) when opened / docsha changes
  const loadPage = async (sha: string, activepage = 1) => {
    setLoading(true)
    setDocPreview(null)
    try {
      setDocPreview(await fetchDocSections(sha, activepage))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && docsha) {
      loadPage(docsha, 1)
    } else if (!open) {
      setDocPreview(null)
    }
  }, [open, docsha])

  // Global resize handlers for the thumbnail column divider
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      setThumbWidth(Math.max(0, startWidthRef.current + delta))
    }
    const onUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = thumbWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const savePage = async (page: KbDocPage, html: string): Promise<void> => {
    await saveDocPage(page.page, page.pagesha, page.filesha, html)
  }

  const pagetotal = docPreview?.docpages?.[0]?.pagetotal ?? 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{ 'data-testid': 'dp_dlg', sx: { height: '90vh' } } as any}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box data-testid="dp_header" sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, gap: 1.5, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" data-testid="dp_filename"
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
            {docPreview?.filename ?? ''}
          </Typography>
          {docPreview && (
            <>
              <Chip size="small" data-testid="dp_words_chip"
                label={`${docPreview.totalwords?.toLocaleString()} words`} sx={{ fontSize: '0.7rem', height: 20 }} />
              <Chip size="small" data-testid="dp_pages_chip"
                label={`${pagetotal} pages`} sx={{ fontSize: '0.7rem', height: 20 }} />
              <Chip size="small" data-testid="dp_uploaded_chip"
                label={docPreview.uploaded} sx={{ fontSize: '0.7rem', height: 20 }} />
            </>
          )}
          {pagetotal > 0 && (
            <Box data-testid="dp_pager" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Previous page">
                <span>
                  <IconButton size="small" data-testid="dp_prev_btn"
                    disabled={!docPreview || docPreview.activepage <= 1}
                    onClick={() => docsha && loadPage(docsha, (docPreview?.activepage ?? 1) - 1)}>
                    <ChevronLeftIcon sx={{ fontSize: ICON_LG }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" data-testid="dp_pager_label"
                sx={{ minWidth: 60, textAlign: 'center' }}>
                {docPreview ? `${docPreview.docpages[0]?.page ?? ''}–${docPreview.docpages[docPreview.docpages.length - 1]?.page ?? ''} of ${pagetotal}` : ''}
              </Typography>
              <Tooltip title="Next page">
                <span>
                  <IconButton size="small" data-testid="dp_next_btn"
                    disabled={!docPreview || (docPreview.docpages[docPreview.docpages.length - 1]?.page ?? 0) >= pagetotal}
                    onClick={() => docsha && loadPage(docsha, (docPreview?.activepage ?? 1) + 1)}>
                    <ChevronRightIcon sx={{ fontSize: ICON_LG }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
          <IconButton size="small" data-testid="dp_close_btn" onClick={onClose}>
            <CloseIcon sx={{ fontSize: ICON_LG }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflowY: 'auto' }}>
        {loading ? (
          <Box data-testid="dp_loading"
            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 1.5, mt: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" sx={{ opacity: 0.6 }}>Retrieving document...</Typography>
          </Box>
        ) : !docPreview?.docpages?.length ? (
          <Box data-testid="dp_empty" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.45 }}>No content</Typography>
          </Box>
        ) : (
          docPreview.docpages.map((page) => (
            <Box key={page.pagesha}
              data-testid="dp_page_row"
              data-page-sha={page.pagesha}
              data-page-num={page.page}
              sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>

              <Box data-testid="dp_page_thumb_col"
                sx={{ width: thumbWidth, flexShrink: 0, display: 'flex', flexDirection: 'column', p: 1, gap: 0.75, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" sx={{ opacity: 0.55 }}>Page</Typography>
                  <Typography variant="caption" data-testid="dp_page_num" sx={{ fontWeight: 600 }}>{page.page}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.55, ml: 1 }}>Words</Typography>
                  <Typography variant="caption" data-testid="dp_page_tokens" sx={{ fontWeight: 600 }}>{page.tokencount}</Typography>
                </Box>
                {fileExt(page.filename) !== 'TXT' && (page.thumb ? (
                  <Box component="img" data-testid="dp_page_thumb" src={page.thumb} alt={`Page ${page.page}`}
                    sx={{ width: '100%', borderRadius: 0.5, border: 1, borderColor: 'divider', objectFit: 'contain' }} />
                ) : (
                  <Box data-testid="dp_page_thumb_placeholder"
                    sx={{ width: '100%', aspectRatio: '3/4', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 0.5 }}>
                    <ImageOutlinedIcon sx={{ fontSize: 32, opacity: 0.3 }} />
                  </Box>
                ))}
              </Box>

              <Box
                data-testid="dp_page_resizer"
                onMouseDown={handleResizeStart}
                sx={{
                  width: 7, flexShrink: 0,
                  borderLeft: 1, borderRight: 1, borderColor: 'divider',
                  bgcolor: 'background.paper',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'col-resize !important',
                  '& *': { cursor: 'col-resize !important' },
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
              </Box>

              <DocPageEditor page={page} onSave={savePage} />

            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  )
}
